import { api, ErroApi } from '../api/cliente';

/**
 * TRANSPORTE DO CHAT — isolado de propósito.
 *
 * Hoje é polling adaptativo sobre D1. O dia em que o volume justificar um
 * Durable Object com WebSocket, a troca acontece NESTE arquivo: o resto do app
 * fala com `assinarConversa` e `enviarMensagem`, não com `fetch`.
 *
 * Gatilho objetivo da migração, decidido antes e não no calor do incidente:
 * p95 de conversas simultaneamente abertas acima de 500. Abaixo disso, um DO
 * por conversa é complexidade paga sem retorno — e as mensagens precisam estar
 * no D1 de qualquer forma, para denúncia, disputa e exportação LGPD.
 */

export interface Mensagem {
  id: string;
  seq: number;
  autor_id: string;
  corpo: string | null;
  tipo: 'texto' | 'foto' | 'sistema';
  arquivo_id: string | null;
  alt_texto: string | null;
  client_uuid: string | null;
  criado_em: number;
}

export interface ResumoConversa {
  conversa_id: string;
  ultimo_seq: number;
  ultima_msg_em: number | null;
  lido_ate: number;
  nao_lidas: number;
}

/**
 * Cadências. A conversa aberta e quente atualiza rápido; o resto espaça.
 *
 * Com a aba escondida o laço PARA: continuar consultando o servidor enquanto
 * ninguém olha gasta bateria do celular de quem está esperando o eletricista.
 */
export const CADENCIA = {
  conversaAtiva: 3_000,
  conversaOciosa: 10_000,
  lista: 30_000,
  /** Tempo sem mensagem nova a partir do qual a conversa é "ociosa". */
  limiarOcioso: 120_000,
} as const;

export function intervaloDe(opcoes: {
  conversaAberta: boolean;
  ultimaMensagemEm: number | null;
  agora?: number;
}): number | false {
  // `false` desliga o refetch do react-query.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  if (!opcoes.conversaAberta) return CADENCIA.lista;

  const agora = opcoes.agora ?? Date.now();
  const desde = opcoes.ultimaMensagemEm ? agora - opcoes.ultimaMensagemEm * 1000 : Infinity;
  return desde < CADENCIA.limiarOcioso ? CADENCIA.conversaAtiva : CADENCIA.conversaOciosa;
}

export interface ConversaDaCaixa {
  id: string;
  chamado_id: string;
  ultimo_seq: number;
  ultima_msg_em: number | null;
  ultima_msg_previa: string | null;
  fechada_em: number | null;
  chamado_titulo: string;
  chamado_status: string;
  lido_ate: number;
  nao_lidas: number;
  meu_papel: 'cliente' | 'profissional';
  outro_nome: string | null;
}

export const conversasQuery = {
  queryKey: ['conversas', 'lista'] as const,
  queryFn: () => api.get<{ conversas: ConversaDaCaixa[] }>('/conversas'),
};

export const resumoQuery = {
  queryKey: ['conversas', 'resumo'] as const,
  queryFn: () => api.get<{ conversas: ResumoConversa[] }>('/conversas/resumo'),
};

/** Busca só o delta. Pedir a conversa inteira a cada 3s seria absurdo. */
export const mensagensQuery = (conversaId: string, desdeSeq: number) => ({
  queryKey: ['conversa', conversaId, desdeSeq] as const,
  queryFn: () => api.get<{ mensagens: Mensagem[]; ultimo_seq: number; fechada: boolean }>(
    `/conversas/${conversaId}/mensagens?desde_seq=${desdeSeq}`,
  ),
});

export function enviarMensagem(conversaId: string, corpo: string, clientUuid: string) {
  return api.post<{ mensagem: { id: string; seq: number; duplicada: boolean } }>(
    `/conversas/${conversaId}/mensagens`, { corpo, client_uuid: clientUuid },
  );
}

/**
 * Mandar uma foto na conversa.
 *
 * Multipart, então vai por `fetch` direto — o `api` serializa JSON. A foto
 * entra como classe 'chamado': privada, servida só às duas partes, com
 * `Cache-Control: no-store`. É o interior da casa de alguém.
 */
export async function enviarFotoNaConversa(
  conversaId: string, arquivo: File, altTexto: string, clientUuid: string,
) {
  const corpo = new FormData();
  corpo.set('arquivo', arquivo);
  corpo.set('alt_texto', altTexto);
  corpo.set('client_uuid', clientUuid);

  const r = await fetch(`/api/conversas/${conversaId}/anexos`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'fetch' },
    body: corpo,
  });
  const resposta: unknown = await r.json().catch(() => null);
  if (!r.ok) {
    const e = (resposta as { erro?: { codigo?: string; mensagem?: string } } | null)?.erro;
    throw new ErroApi(
      r.status, e?.codigo ?? 'interno',
      e?.mensagem ?? 'Não conseguimos enviar a foto. Tente de novo.',
    );
  }
  return resposta as { mensagem: { id: string; seq: number } };
}

export function marcarLida(conversaId: string, ateSeq: number) {
  return api.post<void>(`/conversas/${conversaId}/lida?ate_seq=${ateSeq}`);
}

/**
 * Reconcilia a mensagem otimista com a que voltou do servidor.
 *
 * A mensagem aparece na tela antes de existir no servidor (senão o app parece
 * travado em rede ruim). Quando a real chega com o mesmo `client_uuid`, a
 * otimista some — sem isso a pessoa veria a própria mensagem duas vezes.
 */
export function reconciliar(locais: Mensagem[], doServidor: Mensagem[]): Mensagem[] {
  const uuidsDoServidor = new Set(
    doServidor.map((m) => m.client_uuid).filter((u): u is string => Boolean(u)),
  );
  const seqsConhecidos = new Set(doServidor.map((m) => m.seq));

  const pendentes = locais.filter(
    (m) => m.seq === 0 && m.client_uuid && !uuidsDoServidor.has(m.client_uuid),
  );
  const confirmadas = locais.filter((m) => m.seq > 0 && !seqsConhecidos.has(m.seq));

  return [...confirmadas, ...doServidor].sort((a, b) => a.seq - b.seq).concat(pendentes);
}

export function novoClientUuid(): string {
  return crypto.randomUUID();
}
